/**
 * Every word on the round-the-clock screens.
 *
 * The rules are `ArrangementBuilder/copy.ts`'s: one short sentence; say what HAPPENS rather
 * than what a field IS; put it where the mistake is made rather than where it is
 * discovered. Written for a clerk who has never seen the system — the words "deployment",
 * "slot", "materialise", "horizon" and "policy version" appear nowhere.
 */
export const RTC_COPY = {
  // ── The coverage bar ─────────────────────────────────────────────────────────────
  coverageBar: 'The bar is the whole 24 hours: anything not covered is a time when nobody is on duty.',
  coverageAdvisory:
    'Gaps and overlaps are worth a look, but they never stop you saving — you may have a reason for them.',

  // ── The two timers, which are settings rather than fields on this form ───────────
  horizonDays: 'How many days of orders are kept ready ahead of today, so a division can see what is coming.',
  fillOpenLeadHours: 'How long before a duty starts the divisions are asked to name the officers for it.',
  timersAreSettings:
    'Both are set for the whole system, not per order, so every round-the-clock order uses the same two.',

  // ── Rolling ──────────────────────────────────────────────────────────────────────
  rolling: 'Rolling means the next few days of orders are created for you, again and again, until you end this one.',

  // ── Ending ───────────────────────────────────────────────────────────────────────
  endConfirmTitle: 'End this round-the-clock order?',
  endingWhatItDoes: 'Ending it stops any new orders being created from now on.',
  endingWhatItDoesNot: 'The orders already created keep their duties and the officers named on them.',
  endingIsFinal: 'There is no way to restart it afterwards — you would raise a new one.',

  // ── Why there is no command section ──────────────────────────────────────────────
  noSupervision: 'Command is named on each order as it is created, not here.',
  noSupervisionWhy:
    'Who is in charge at 3 a.m. changes night by night, so naming it once here would be wrong most nights.',

  // ── The rest of the form ─────────────────────────────────────────────────────────
  title: 'What this cover is called — "Park Street naka", "Maidan night patrol".',
  startDate: 'The first day orders are created for.',
  endsNever: 'It keeps running until you end it.',
  endsOnDate: 'It stops creating orders after this date.',
  oneRequirement: 'Every shift asks for the same force, so you set it once here.',
  divisionsDerived: 'The divisions come from the rows you add below — there is nothing else to choose.',
  shiftsSeeded: 'Three reliefs are filled in to start with. Change the times, or add a fourth.',
  addShift: 'Add shift',

  // ── Saving ───────────────────────────────────────────────────────────────────────
  save: 'Save and start',
  saveChanges: 'Save changes',
  saveHint: 'Saving is what starts the orders being created, so this form has no autosave.',
  saveChangesActive:
    'Orders already created that nobody has been named to yet will be re-made from the new details. Ones already being filled are left exactly as they are.',
  unsavedGuard: 'You have changes that have not been saved yet. Leave anyway?',
  unsaved: 'Not saved yet',
  saved: 'Saved',

  // ── The preview ──────────────────────────────────────────────────────────────────
  previewPending: 'Working out how many orders this makes…',
  previewNotYet: 'Choose a policy, a start date and the shifts to see how many orders this makes.',
  willGenerate: (input: { willCreate: number; horizonDays: number; isRolling: boolean }): string => {
    const orders = `${String(input.willCreate)} order${input.willCreate === 1 ? '' : 's'}`;
    const days = `${String(input.horizonDays)} day${input.horizonDays === 1 ? '' : 's'}`;
    return `About ${orders} over the next ${days}${input.isRolling ? ', rolling' : ''}.`;
  },
} as const;

export const RTC_LIST_COPY = {
  title: 'Round the clock',
  create: 'New round-the-clock order',
  emptyTitle: 'No round-the-clock orders yet.',
  emptyDescription: 'Raise one for a duty that runs every day, like a picket or a night patrol.',
  emptySearch: 'No round-the-clock orders match this search.',
  generatedHint: 'How many arrangements this order has created so far.',
} as const;

/** ⚠️ Derived from the shift pattern by `lib/shiftCoverage.ts` — never a stored string. */
export const COVERAGE_LABEL = {
  ROUND_THE_CLOCK: 'Round the clock',
  GAPS: 'Gaps',
  OVERLAPS: 'Overlaps',
  NONE: 'No shifts yet',
} as const;
